import React, { useState, useCallback, useEffect } from "react";
import { Button as BtnPrimary } from "@talentbait/shared-components";

import "emoji-mart/css/emoji-mart.css";
import { Picker } from "emoji-mart";
import * as SC from "./Campaign__Create_TeaserEditor.styled";

import { SidePanel, Skeleton, CollapsibleText } from "../Components";
import TeaserOptions from "../Campaign__Create_TeaserOptions.jsx";
import { noop } from "../utils";
import useComponentVisible from "../../../../hooks/useComponentVisible";
import { withRefluxStore, PreviewStore } from "../../../../Stores";
import Actions from "../../../../Actions";
import { EmojiIcon } from "../Svg";
import logger from "../../../../utils/LoggerFrontend.js";
import { useTranslation } from "react-i18next";
import useTeaserInputValidation from "../hooks/useTeaserInputValidation";
import { darkChannels, shortChannels } from "./utils";
import { Tooltip } from "../../../../Components/Overlays";

const CampaignCreateTeaserEditor = ({
  channel,
  updatePost,
  teaserText,
  teaserTextStyle = {},
  smallButtons,
  limit,
  editable,
  minRows = 6,
  lines = 6,
  padding = "6px 10px",
  dark,
  allowEmpty,
  bannedCharaters = [],
  isAutoGenerated = false,
  autoGeneratedText,
  rejectUppercaseWords = false,
  rejectPhoneNumbers = false,
  uppercaseWhitelist = [],
  disableEmojis = false,
  postDataKey = "teaserText",
  showTeaserRecommendations = true,
  showMoreButton = true,
  placeholder = "",
  selectedTextIndex = -1,
  disableCheckmarks = false,
  rejectHashtags = false,
  // Preview Store
  loadingTeaserOptions,
  defaultOpenTeaserEditor,
  adErrors
}) => {
  logger.debug("CampaignCreateTeaserEditor", "called", {
    channel,
    selectedTextIndex,
    teaserText,
    autoGeneratedText,
    isAutoGenerated
  });

  const { t } = useTranslation();
  const [showEmojis, setShowEmojis] = useState(false);
  const [showOptions, setShowOptions] = useState(defaultOpenTeaserEditor);
  const [teaserInputText, setTeaserInputText] = useState(
    selectedTextIndex !== -1 ? teaserText[selectedTextIndex] : teaserText
  );

  const [isChangedAnimation, setIsChangedAnimation] = useState(false);
  const isLoadingAutoGenerated = isAutoGenerated && !autoGeneratedText && !teaserText;

  const closeEditingCallback = useCallback(() => {
    setShowOptions(false);
    setShowEmojis(false);
    if (selectedTextIndex != -1) {
      const newTeaserText = [...teaserText];
      newTeaserText[selectedTextIndex] = teaserInputText;
      updatePost({ [postDataKey]: newTeaserText });
    } else {
      updatePost({
        [postDataKey]: teaserInputText
      });
    }
    if (defaultOpenTeaserEditor) {
      Actions.turnOffDefaultOpenTeaserEditor();
    }
  }, [
    defaultOpenTeaserEditor,
    teaserInputText,
    updatePost,
    postDataKey,
    teaserText,
    selectedTextIndex
  ]);

  const {
    ref: editorRef,
    isComponentVisible: editingTeaser,
    setIsComponentVisible: setEditingTeaser
  } = useComponentVisible(defaultOpenTeaserEditor, closeEditingCallback);

  useEffect(() => {
    setTeaserInputText(selectedTextIndex !== -1 ? teaserText[selectedTextIndex] : teaserText);
  }, [teaserText, selectedTextIndex, updatePost]);

  const stopIsChangedAnimation = useCallback(() => setIsChangedAnimation(false), []);

  const updateTeaserInputText = useCallback(
    (event) => {
      if (disableCheckmarks) {
        return setTeaserInputText((event.target.value || "").replace(/^✅|\n✅/g, ""));
      }
      return setTeaserInputText(event.target.value || "");
    },
    [disableCheckmarks]
  );

  const onFinishTyping = useCallback(() => {
    closeEditingCallback();
  }, [closeEditingCallback]);

  const toggleEmojis = useCallback(() => {
    setShowOptions(false);
    setShowEmojis((show) => !show);
  }, []);

  const [cursorPosition, setCursorPosition] = useState(0);
  const handleClickInput = useCallback((e) => setCursorPosition(e.target.selectionStart), []);
  const addEmoji = useCallback(
    (emoji) => {
      const newText =
        teaserInputText.slice(0, cursorPosition) +
        (emoji?.native || "") +
        teaserInputText.slice(cursorPosition);
      setTeaserInputText(newText);
    },
    [cursorPosition, teaserInputText]
  );

  const toggleOptions = useCallback(() => {
    setShowEmojis(false);
    setShowOptions((show) => !show);
  }, []);

  const applyOption = useCallback(
    (option) => {
      setIsChangedAnimation(true);
      setTeaserInputText(limit ? option.substring(0, limit) : option);
      setShowOptions(false);
    },
    [limit]
  );

  const enterEditing = useCallback(() => {
    setEditingTeaser(true);
  }, [setEditingTeaser]);

  const cancelEditing = useCallback(() => {
    setTeaserInputText(selectedTextIndex !== -1 ? teaserText[selectedTextIndex] : teaserText);
    setEditingTeaser(false);
  }, [setEditingTeaser, selectedTextIndex, teaserText]);

  const saveEditing = useCallback(
    (event) => {
      event.preventDefault();
      setIsChangedAnimation(true);
      if (selectedTextIndex != -1) {
        const newTeaserText = [...teaserText];
        newTeaserText[selectedTextIndex] = event.target[postDataKey].value;
        updatePost({ [postDataKey]: newTeaserText });
      } else {
        updatePost({ [postDataKey]: event.currentTarget.elements[postDataKey]?.value });
      }
      setEditingTeaser(false);
    },
    [setEditingTeaser, updatePost, postDataKey, selectedTextIndex, teaserText]
  );

  //Validator
  const { valid, error } = useTeaserInputValidation(
    teaserInputText,
    bannedCharaters,
    disableEmojis,
    rejectUppercaseWords,
    rejectPhoneNumbers,
    uppercaseWhitelist,
    channel,
    adErrors,
    postDataKey,
    isLoadingAutoGenerated,
    rejectHashtags
  );

  const isShortTeaserText = shortChannels.includes(channel) || teaserInputText?.length < 50;

  return (
    <SC.TeaserEditorWrapper
      onSubmit={saveEditing}
      isChangedAnimation={isChangedAnimation}
      ref={editorRef}
      dark={dark}
    >
      {defaultOpenTeaserEditor && loadingTeaserOptions ? (
        <Skeleton width="100%" height="145px" />
      ) : editingTeaser ? (
        <>
          <SC.TeaserInput
            onClick={handleClickInput}
            onAnimationEnd={stopIsChangedAnimation}
            minRows={minRows}
            value={teaserInputText}
            onChange={updateTeaserInputText}
            name={postDataKey}
            maxLength={limit}
            style={teaserTextStyle}
            padding={padding}
            placeholder={placeholder}
            maxRows={20}
            // ref={inputRef}
          />
          <SC.InputLimit style={teaserTextStyle}>
            {limit
              ? `${teaserInputText?.length || 0}/${limit}`
              : `${teaserInputText?.length || 0} ${t("characters")}`}
          </SC.InputLimit>
        </>
      ) : (
        <Tooltip
          label={t("Click to edit text")}
          enabled={editable}
          containerStyles={{ display: "inherit" }}
        >
          <SC.TeaserTextWrapper
            onAnimationEnd={stopIsChangedAnimation}
            editable={editable}
            onClick={editable ? enterEditing : noop}
            dark={dark}
            allowEmpty={allowEmpty}
            padding={padding}
            isShort={isShortTeaserText}
            isAutoGenerated={isAutoGenerated}
          >
            <CollapsibleText
              lines={lines}
              text={teaserInputText}
              textStyle={teaserTextStyle}
              showMoreButton={showMoreButton}
              placeholder={placeholder}
              isLoading={isLoadingAutoGenerated}
              isAutoGenerated={isAutoGenerated}
              onFinishTyping={onFinishTyping}
              isShort={shortChannels.includes(channel)}
              isDark={dark || darkChannels.includes(channel)}
            />
          </SC.TeaserTextWrapper>
        </Tooltip>
      )}
      {editingTeaser && teaserInputText?.length > limit && (
        <SC.ErrorMessage className="preview-error">
          Text is too long. The character limit is {limit}
        </SC.ErrorMessage>
      )}
      {!!error && <SC.ErrorMessage className="preview-error">{error}</SC.ErrorMessage>}
      <SC.EditorActions
        editingTeaser={editingTeaser && (!defaultOpenTeaserEditor || !loadingTeaserOptions)}
      >
        <SC.EditorInsertActions>
          {showTeaserRecommendations && (
            <BtnPrimary
              size="medium"
              variant="outline"
              type="tertiary"
              mr={"5px"}
              px={"5px"}
              onClick={toggleOptions}
              small={smallButtons}
            >
              {smallButtons ? "Alternative" : "Alternative wählen"}
            </BtnPrimary>
          )}
          {!disableEmojis && (
            <BtnPrimary
              size="medium"
              variant="outline"
              type="tertiary"
              mr={"5px"}
              px={"5px"}
              onClick={toggleEmojis}
              small={smallButtons}
            >
              <EmojiIcon />
            </BtnPrimary>
          )}
        </SC.EditorInsertActions>
        <SC.EditorUpdateActions>
          <SC.CancelButtonWrapper small={smallButtons}>
            <BtnPrimary
              size="medium"
              variant="outline"
              type="tertiary"
              mr={"5px"}
              px={"5px"}
              onClick={cancelEditing}
              small={smallButtons}
            >
              {t("Cancel")}
            </BtnPrimary>
          </SC.CancelButtonWrapper>
          <BtnPrimary
            size="medium"
            type="secondary"
            px={"5px"}
            small={smallButtons}
            disabled={
              !valid || (!allowEmpty && !teaserInputText?.length) || teaserInputText?.length > limit
            }
          >
            {t("Save")}
          </BtnPrimary>
        </SC.EditorUpdateActions>
      </SC.EditorActions>
      {editable && (
        <>
          <SidePanel
            active={showOptions && (!defaultOpenTeaserEditor || !loadingTeaserOptions)}
            side="left"
            offset={10}
          >
            <TeaserOptions
              channel={channel}
              teaserTextStyle={teaserTextStyle}
              applyOption={applyOption}
              dark={dark}
              isAutoGenerated={isAutoGenerated}
              autoGeneratedText={autoGeneratedText}
            />
          </SidePanel>
          <SidePanel
            width="auto"
            height="auto"
            active={showEmojis}
            side="left"
            offset={10}
            borderRadius="6px"
            arrowOffset="0"
          >
            <Picker theme="light" onSelect={addEmoji} title="" emoji="" />
          </SidePanel>
        </>
      )}
    </SC.TeaserEditorWrapper>
  );
};

export default withRefluxStore(
  CampaignCreateTeaserEditor,
  [PreviewStore],
  ["loadingTeaserOptions", "defaultOpenTeaserEditor", "adErrors"]
);
